import React from 'react';
import { Icon } from 'antd';
import {
  useFieldController,
  useFormMethods,
} from '../../../../../../helpers/formManager';
import { fieldNames } from '../../constants';
import { mbToBytes } from '../../../../../../helpers/attachments/mbToBytes';
import Field from '../../../../shared/ui/Field';
import { MAX_FILE_SIZE } from '../../../../shared/configs/attachments';

const UploadField = ({ label, multiple }) => {
  const {
    field: { value, onChange },
    fieldState,
  } = useFieldController({
    name: fieldNames.files,
    defaultValue: [],
    rules: { validate: createValidate(multiple) },
  });

  const { getValues, setValue, clearErrors, trigger } = useFormMethods();

  const beforeUpload = (file) => {
    if (file.size > mbToBytes(50)) {
      file.status = 'error';
      file.response =
        'Размер файла превышает 50 Мб. Он не будет отправлен на сервер';
    } else {
      file.status = 'done';
    }

    return false;
  };

  const handleChange = ({ file, fileList }) => {
    const dependentFields = [fieldNames.name, fieldNames.description];

    const [name, description] = getValues(dependentFields);

    if (multiple) {
      if (
        file.status === 'removed' ||
        fileList[fileList.length - 1].uid === file.uid // чтобы не дублировать добавление файла в fileList, проверяем, что последний файл в списке — это тот же файл, который был изменен
      ) {
        onChange(fileList);

        if (fileList.length > 1) {
          // если в списке несколько файлов, очищаем поля name и description
          if (name) {
            setValue(fieldNames.name, '');
          }

          if (description) {
            setValue(fieldNames.description, '');
          }

          clearErrors(dependentFields);

          return;
        }

        const [firstFile] = fileList;

        // если файл один, он валидный и поле name пустое, заполняем его именем файла без расширения
        if (!name.trim() && firstFile && firstFile.status !== 'error') {
          const fileNameChunks = firstFile.name.split('.');
          fileNameChunks.pop();
          setValue(fieldNames.name, fileNameChunks.join('.'));
          trigger(fieldNames.name);
        }
      }
    } else {
      const newValue = fileList.status === 'removed' ? [] : [file];
      onChange(newValue);

      if (!name.trim() && file.status === 'done') {
        const fileNameChunks = file.name.split('.');
        fileNameChunks.pop();
        setValue(fieldNames.name, fileNameChunks.join('.'));
        trigger(fieldNames.name);
      }
    }
  };

  return (
    <Field.Memoized
      type={Field.types.uploadDragger}
      formItemLabel={label}
      required
      fileList={value}
      errorMessage={fieldState.error?.message}
      beforeUpload={beforeUpload}
      multiple={multiple}
      onChange={handleChange}
    >
      <div>
        <Icon type="file-add" theme="outlined" /> Перетащите файл или нажмите,
        чтобы выбрать
      </div>
      <p>Максимальный размер файла: {MAX_FILE_SIZE} Мб</p>
    </Field.Memoized>
  );
};

export default UploadField;

function createValidate(multiple) {
  return (value) => {
    if (!value?.length) {
      return multiple ? 'Выберите файл(ы)' : 'Выберите файл(ы)';
    }

    if (value.every((f) => f.status === 'error')) {
      return multiple
        ? 'Все файлы превышают допустимый размер'
        : 'Файл превышает допустимый размер';
    }

    return true;
  };
}
